import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { COUNTRIES, TZ_COUNTRY } from '@/config/phoneCountries';

/*
  Teléfono con selector de país (bandera + prefijo) y campo para el número (10/10/2026).

  - Valor que entrega: «+34 612345678» (prefijo, espacio y número nacional sin el 0 troncal).
  - País por defecto: el de la zona horaria del dispositivo; si no se sabe, el idioma del navegador
    (es-MX → México); si tampoco, España. No se envía nada a terceros para detectarlo.
  - Si alguien pega un número internacional completo en el campo, se cambia el país solo.
*/

const BY_ISO = Object.fromEntries(COUNTRIES.map((c) => [c.iso, c]));
// Para «+1», «+44», «+7»… el país principal cuando no hay otra pista
const MAIN_FOR_DIAL = { 1: 'US', 7: 'RU', 44: 'GB', 39: 'IT', 47: 'NO', 61: 'AU', 212: 'MA', 262: 'RE', 290: 'SH', 358: 'FI', 590: 'GP', 599: 'CW' };
const DIALS = [...new Set(COUNTRIES.map((c) => c.dial))].sort((a, b) => b.length - a.length);

// Textos del selector en la web pública traducida (lang='en'); los nombres de país en inglés los da el navegador
const L = {
  es: { number: 'Número', search: 'Buscar país o prefijo', none: 'No hay resultados', country: 'País', phone: 'Teléfono', prefix: (n, d) => `Prefijo: ${n} +${d}. Cambiar país` },
  en: { number: 'Number', search: 'Search country or code', none: 'No results', country: 'Country', phone: 'Phone', prefix: (n, d) => `Code: ${n} +${d}. Change country` },
};
let EN_NAMES = null;
function countryName(c, lang) {
  if (lang !== 'en') return c.name;
  try {
    EN_NAMES = EN_NAMES || new Intl.DisplayNames(['en-GB'], { type: 'region' });
    return EN_NAMES.of(c.iso) || c.name;
  } catch { return c.name; }
}

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function detectCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && TZ_COUNTRY[tz] && BY_ISO[TZ_COUNTRY[tz]]) return TZ_COUNTRY[tz];
  } catch { /* sin Intl */ }
  try {
    const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const l of langs) {
      const region = String(l || '').split('-')[1];
      if (region && BY_ISO[region.toUpperCase()]) return region.toUpperCase();
    }
  } catch { /* sin navigator */ }
  return 'ES';
}

// «+34 612 34 56 78» → { iso, national }
export function parsePhone(value, preferIso) {
  const raw = String(value || '').trim();
  if (!raw.startsWith('+') && !raw.startsWith('00')) return null;
  const digits = raw.replace(/^00/, '').replace(/\D/g, '');
  const dial = DIALS.find((d) => digits.startsWith(d));
  if (!dial) return null;
  const iso = (preferIso && BY_ISO[preferIso]?.dial === dial) ? preferIso
    : (MAIN_FOR_DIAL[dial] || COUNTRIES.find((c) => c.dial === dial)?.iso);
  return { iso, national: digits.slice(dial.length) };
}

function cleanNational(digits, country) {
  let d = String(digits || '').replace(/\D/g, '');
  if (country?.np && d.length > (country.min || 0) && d.startsWith(country.np)) d = d.slice(country.np.length);
  return d;
}

/** ¿Es un teléfono válido? (longitud posible del número nacional según el país) */
export function isValidPhone(value) {
  const p = parsePhone(value);
  if (!p) return false;
  const c = BY_ISO[p.iso];
  const n = p.national.length;
  return !!c && n >= (c.min || 6) && n <= (c.max || 15);
}

const Flag = ({ iso, className = '' }) => (
  <img src={`/flags/${iso}.svg`} alt="" loading="lazy" width="20" height="14"
    className={`w-5 h-[14px] rounded-[2px] object-cover shrink-0 shadow-[0_0_0_1px_rgba(0,0,0,0.08)] ${className}`} />
);

const STYLES = {
  light: {
    wrap: 'border border-slate-200 bg-white focus-within:border-[#D4AF37] focus-within:ring-2 focus-within:ring-[#D4AF37]/20',
    btn: 'text-[#0F172A] hover:bg-slate-50 border-r border-slate-200',
    input: 'text-[#0F172A] placeholder:text-slate-400',
    menu: 'bg-white border border-slate-200 text-[#0F172A]',
    search: 'border-b border-slate-100 text-[#0F172A] placeholder:text-slate-400',
    item: 'hover:bg-slate-50', active: 'bg-[#D4AF37]/10', muted: 'text-slate-400',
  },
  dark: {
    wrap: 'border border-white/10 bg-white/[0.03] focus-within:border-[#c9a84c]/60',
    btn: 'text-white hover:bg-white/[0.05] border-r border-white/10',
    input: 'text-white placeholder:text-white/30',
    menu: 'bg-[#0d1829] border border-white/10 text-white',
    search: 'border-b border-white/[0.06] text-white placeholder:text-white/30 bg-transparent',
    item: 'hover:bg-white/[0.05]', active: 'bg-[#c9a84c]/10', muted: 'text-white/40',
  },
};

export default function PhoneInput({ value, onChange, required = false, disabled = false, variant = 'light', id, testId = 'phone',
  className = '', heightClass = 'h-12', ariaLabel, rounded = 'rounded-none', lang = 'es' }) {
  const tl = L[lang === 'en' ? 'en' : 'es'];
  const st = STYLES[variant] || STYLES.light;
  const initial = useMemo(() => parsePhone(value) || { iso: detectCountry(), national: '' }, []);
  const [iso, setIso] = useState(initial.iso);
  const [national, setNational] = useState(initial.national);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const country = BY_ISO[iso] || BY_ISO.ES;

  // Si el valor cambia desde fuera (p. ej. al vaciar el formulario), se sincroniza
  useEffect(() => {
    const p = parsePhone(value, iso);
    if (!value) { setNational(''); return; }
    if (p && (p.iso !== iso || p.national !== national)) { setIso(p.iso); setNational(p.national); }
  }, [value]);

  const emit = (nextIso, nextNational) => {
    const c = BY_ISO[nextIso];
    onChange?.(nextNational ? `+${c.dial} ${nextNational}` : '');
  };

  const list = useMemo(() => {
    const t = norm(q).replace(/^\+/, '');
    const items = !t ? COUNTRIES : COUNTRIES.filter((c) => norm(c.name).includes(t) || norm(countryName(c, lang)).includes(t) || c.dial.startsWith(t) || c.iso.toLowerCase() === t);
    // España primero cuando no se busca nada
    if (!t) return [BY_ISO.ES, ...items.filter((c) => c.iso !== 'ES')];
    return items;
  }, [q, lang]);

  useEffect(() => {
    if (!open) return undefined;
    setHi(Math.max(0, list.findIndex((c) => c.iso === iso)));
    const t = setTimeout(() => searchRef.current?.focus(), 30);
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onDown); document.removeEventListener('touchstart', onDown); };
  }, [open]);

  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-idx="${hi}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);

  const pick = (c) => {
    setIso(c.iso);
    setOpen(false);
    setQ('');
    const n = cleanNational(national, c);
    setNational(n);
    emit(c.iso, n);
  };

  const onNumber = (e) => {
    const v = e.target.value;
    // Han pegado un número internacional completo: cambia el país
    const p = parsePhone(v, iso);
    if (p && p.iso) {
      const c = BY_ISO[p.iso];
      const n = cleanNational(p.national, c);
      setIso(p.iso); setNational(n); emit(p.iso, n);
      return;
    }
    const n = v.replace(/[^\d]/g, '').slice(0, 15);
    setNational(n);
    emit(iso, cleanNational(n, country));
  };

  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(list.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (list[hi]) pick(list[hi]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  };

  return (
    <div ref={boxRef} className={`relative ${className}`} data-testid={testId}>
      <div className={`flex items-stretch overflow-hidden ${heightClass} ${rounded} ${st.wrap} ${disabled ? 'opacity-60' : ''}`}>
        <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox" aria-expanded={open} aria-label={tl.prefix(countryName(country, lang), country.dial)}
          className={`flex items-center gap-1.5 pl-3 pr-2 min-w-[96px] text-sm font-medium transition-colors ${st.btn}`}
          data-testid={`${testId}-country`}>
          <Flag iso={country.iso} />
          <span className="tabular-nums">+{country.dial}</span>
          <ChevronDown size={14} className={`ml-auto opacity-60 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        <input id={id} type="tel" inputMode="tel" autoComplete="tel-national" value={national} onChange={onNumber}
          required={required} disabled={disabled} aria-label={ariaLabel || tl.phone}
          placeholder={country.ex ? country.ex.replace(/(\d{3})(?=\d)/g, '$1 ') : tl.number}
          className={`flex-1 min-w-0 bg-transparent px-3 text-base sm:text-sm outline-none ${st.input}`}
          data-testid={`${testId}-number`} />
      </div>

      {open && (
        <div className={`absolute left-0 right-0 sm:right-auto sm:w-[340px] top-full mt-1 z-[60] shadow-2xl rounded-lg overflow-hidden ${st.menu}`}>
          <div className={`flex items-center gap-2 px-3 ${st.search}`}>
            <Search size={15} className={st.muted} />
            <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey}
              placeholder={tl.search} aria-label={tl.search}
              className="flex-1 h-11 bg-transparent outline-none text-sm" data-testid={`${testId}-search`} />
          </div>
          <ul ref={listRef} role="listbox" aria-label={tl.country} className="max-h-64 overflow-y-auto py-1">
            {list.length === 0 && <li className={`px-4 py-3 text-sm ${st.muted}`}>{tl.none}</li>}
            {list.map((c, i) => (
              <li key={c.iso} role="option" aria-selected={c.iso === iso} data-idx={i}>
                <button type="button" onClick={() => pick(c)} onMouseEnter={() => setHi(i)}
                  className={`w-full flex items-center gap-3 px-4 min-h-[44px] text-left text-sm ${st.item} ${i === hi ? st.active : ''}`}>
                  <Flag iso={c.iso} />
                  <span className="flex-1 truncate">{countryName(c, lang)}</span>
                  <span className={`tabular-nums ${st.muted}`}>+{c.dial}</span>
                  {c.iso === iso && <Check size={14} className="text-[#c9a84c]" />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
